import { z } from "zod";

/**
 * Machine à états d'un participant.
 *
 * La soirée Hoa se lit sur une frise : on rejoint une tribu, Hoa pose ses
 * cinq questions, le chat s'ouvre quinze minutes avant le rendez-vous pour
 * tout le monde en même temps, puis on se retrouve sur place.
 *
 * Il n'y a plus d'étape de décision : appuyer sur « J'y vais » **est**
 * l'engagement. Qui change d'avis quitte la tribu, c'est tout. La dernière
 * question collective a disparu elle aussi : les points communs se lisent
 * dans les cinq réponses données en rejoignant.
 *
 * Le serveur refuse toute transition non déclarée ici.
 */
export const STAGES = [
  "entered",
  "located",
  "in_session",
  "named",
  /** A rejoint la tribu ; répond aux questions et attend l'ouverture du chat. */
  "joined",
  /** Le chat de la tribu est ouvert (quinze minutes avant le rendez-vous). */
  "chatting",
  /** L'heure du rendez-vous est passée : on marche, le chat se relit. */
  "rendezvous",
  /** Arrivé sur place. */
  "gathered",
  "released",
  "left",
] as const;

export const zStage = z.enum(STAGES);
export type Stage = z.infer<typeof zStage>;

/** Transitions autorisées. « left » est joignable depuis presque partout. */
const ALLOWED: Record<Stage, readonly Stage[]> = {
  entered: ["located", "left"],
  located: ["in_session", "left"],
  in_session: ["named", "left"],
  named: ["joined", "left"],
  // Qui rejoint après l'ouverture du chat — ou après l'heure du rendez-vous —
  // passe directement à l'étape en cours. « released » : tribu annulée.
  joined: ["chatting", "rendezvous", "released", "left"],
  chatting: ["rendezvous", "released", "left"],
  rendezvous: ["gathered", "released", "left"],
  gathered: ["released", "left"],
  released: [],
  left: [],
};

export function canTransition(from: Stage, to: Stage): boolean {
  return (ALLOWED[from] ?? []).includes(to);
}

export const STAGE_LABELS: Record<Stage, string> = {
  entered: "vient d'entrer",
  located: "confirme son quartier",
  in_session: "est dans la session",
  named: "a choisi son nom",
  joined: "a rejoint sa tribu",
  chatting: "échange avec sa tribu",
  rendezvous: "va au rendez-vous",
  gathered: "a retrouvé sa tribu",
  released: "a terminé sa soirée",
  left: "est parti",
};

/** Ordre d'avancement, pour l'affichage de progression agrégée. */
export function stageIndex(stage: Stage): number {
  const i = STAGES.indexOf(stage);
  return i < 0 ? 0 : i;
}
